// src/components/Transacciones.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaCheckCircle, FaDownload, FaTimes, FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaImage, FaFileImage, FaPaperclip, FaFile } from 'react-icons/fa';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as XLSX from 'xlsx';
import Logo from './Logo';
import { brandIdentity } from '../theme/brandIdentity';

// --- Mapeo de campos con nombres en español y orden específico ---
const FIELDS_MAPPING = {
  "transaction_id": { name: "TRANSACCIÓN", order: 1 },
  "timestamp": { name: "FECHA RETOMA", order: 2 },
  "tradein_status": { name: "FLAG", order: 3 },
  "buyback_device_name_std": { name: "DISPOSITIVO", order: 4 },
  "buyback_device_imei": { name: "IMEI 1", order: 5 },
  "buyback_device_imei_2": { name: "IMEI 2", order: 6 },
  "buyback_device_brand": { name: "FABRICANTE", order: 7 },
  "buyback_device_storage": { name: "GB RETOMA", order: 8 },
  "additional_items": { name: "ADICIONALES", order: 9 },
  "buyback_device_final_grade": { name: "GRADO FINAL", order: 10 },
  "buyback_device_cosmetic_grade": { name: "GRADO COSMÉTICO", order: 11 },
  "buyback_device_functional_grade": { name: "GRADO FUNCIONAL", order: 12 },
  "buyback_device_category": { name: "CATEGORÍA RETOMADA", order: 13 },
  "bonus_amount_trader": { name: "BONO REFURBI", order: 14 },
  "bonus_amount_additional": { name: "BONO ADICIONALES", order: 15 },
  "bonus_amount_campaign": { name: "CAMPAÑA TERCERO", order: 16 },
  "bonus_amount_total": { name: "TOTAL RETOMA", order: 17 },
  "total_refurbi": { name: "TOTAL REFURBI", order: 18 },
  "bonus_amount_currency": { name: "MONEDA", order: 19 },
  "customer_advisor_id": { name: "CEDULA PROMOTOR", order: 20 },
  "customer_advisor_name": { name: "PROMOTOR", order: 21 },
  "promoter_id": { name: "ID USUARIO", order: 22 },
  "promoter_name": { name: "NOMBRE USUARIO", order: 23 },
  "promoter_email": { name: "CORREO PROMOTOR", order: 24 },
  "sold_device_name": { name: "REFERENCIA VENDIDA", order: 25 },
  "sold_device_storage": { name: "GB REFERENCIA VENDIDA", order: 26 },
  "sold_device_price": { name: "PRECIO REFERENCIA VENDIDA", order: 27 },
  "sold_device_category": { name: "CATEGORÍA VENDIDA", order: 28 },
  "store_name": { name: "TIENDA", order: 29 },
  "store_city": { name: "CIUDAD TIENDA", order: 30 },
  "company_name": { name: "COMPAÑÍA", order: 31 },
  "brand": { name: "MARCA", order: 32 },
  "retailer": { name: "RETAIL", order: 33 },
  "customer_customer_name": { name: "NOMBRE CLIENTE", order: 34 },
  "customer_customer_surname": { name: "APELLIDOS CLIENTE", order: 35 },
  "customer_customer_id": { name: "CEDULA CLIENTE", order: 36 },
  "customer_customer_email": { name: "CORREO CLIENTE", order: 37 },
  "customer_customer_address": { name: "DIRECCIÓN CLIENTE", order: 38 },
  "customer_customer_city": { name: "CIUDAD CLIENTE", order: 39 },
  "customer_customer_department": { name: "DEPARTAMENTO CLIENTE", order: 40 },
  "customer_customer_phone": { name: "TELÉFONO CLIENTE", order: 41 },
  "tradein_type": { name: "TIPO DE RETOMA", order: 42 },
  "manual_form_reason": { name: "RETOMA MANUAL - MOTIVO CREACIÓN", order: 43 },
  "review_timestamp": { name: "RETOMA MANUAL - FECHA REVISIÓN", order: 44 },
  "reviewer_id": { name: "RETOMA MANUAL - ID REVISOR", order: 45 },
  "reviewer_name": { name: "RETOMA MANUAL - NOMBRE REVISOR", order: 46 },
  "rejection_reason": { name: "RETOMA MANUAL - RAZÓN RECHAZO", order: 47 },
  "cancelled_by_id": { name: "CANCELADO POR - ID", order: 48 },
  "cancelled_by_name": { name: "CANCELADO POR - NOMBRE", order: 49 },
  "cancelled_by_rol": { name: "CANCELADO POR - ROL", order: 50 },
  "cancelled_date": { name: "CANCELADO POR - FECHA", order: 51 },
  "cancelled_reason": { name: "CANCELADO POR - RAZÓN", order: 52 }
};

// Obtener solo las claves de los campos que queremos mostrar
const ALLOWED_FIELDS = Object.keys(FIELDS_MAPPING);

// Función para obtener campos ordenados por su número de orden
const getOrderedFields = (availableFields) => {
  return availableFields
    .filter(field => FIELDS_MAPPING[field])
    .sort((a, b) => FIELDS_MAPPING[a].order - FIELDS_MAPPING[b].order);
};



function Transacciones() {
  // Estados para datos completos
  const [allTransactions, setAllTransactions] = useState([]); // Todos los datos cargados
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Datos después de aplicar filtros
  const [displayedTransactions, setDisplayedTransactions] = useState([]); // Datos de la página actual

  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('idle'); // 'idle' | 'loading' | 'success'

  // Estados para paginación del lado cliente
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25); // 25 registros por página
  const [totalPages, setTotalPages] = useState(0);

  // Estados para el Panel de Control (similar a DashboardEjecutivo)
  const [controlPanelCollapsed, setControlPanelCollapsed] = useState(true);
  const [searchSectionCollapsed, setSearchSectionCollapsed] = useState(true);

  // Estados para filtros
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    retailer: '',
    brand: '',
    store: ''
  });

  // Estados para datos dinámicos
  const [retailers, setRetailers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);

  // Estados para búsquedas
  const [searchType, setSearchType] = useState('transaction'); // 'imei' | 'transaction'
  const [searchImei, setSearchImei] = useState('');
  const [searchTransactionId, setSearchTransactionId] = useState('');

  // Estado para rol de usuario
  const [userRole, setUserRole] = useState('');

  // Estados para el modal de detalles de transacción
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [transactionImages, setTransactionImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState('');
  const [loadingImages, setLoadingImages] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(false);
  const [isModalInitialLoad, setIsModalInitialLoad] = useState(false);

  // Estados para arrastre y zoom de imágenes
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);

  // Estados para descarga de contrato
  const [loadingContract, setLoadingContract] = useState(false);

  // Estados para archivos adjuntos
  const [transactionFiles, setTransactionFiles] = useState([]);
  const [downloadingFileName, setDownloadingFileName] = useState(null);

  // Estados para cancelación de transacciones
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');

  // Sistema de caché de imágenes
  const [imageCache, setImageCache] = useState(new Map());
  const [cacheOrder, setCacheOrder] = useState([]);

  const navigate = useNavigate();
  const tenantId = localStorage.getItem('tenant_id') || '';

  // Función para obtener fechas dinámicas (últimos 3 días) usando zona horaria local
  const getDefaultDateRange = () => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 2); // 3 días atrás (incluyendo hoy)

    const formatDate = (date) => {
      // Usar zona horaria local en lugar de UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(today)
    };
  };

  // Función para obtener rol del usuario
  const getUserRole = async () => {
    try {
      const savedRole = localStorage.getItem('user_role');
      const cognitoGroups = JSON.parse(localStorage.getItem('cognito_groups') || '[]');

      // Verificar si es admin_tenant basado en los grupos de Cognito (formato: TENANT_ID#admin_tenant)
      const isAdminTenant = cognitoGroups.some(group => group.includes('admin_tenant')) || savedRole === 'admin_tenant';

      setUserRole(isAdminTenant ? 'admin_tenant' : 'user');
      console.log('👤 Rol del usuario:', isAdminTenant ? 'admin_tenant' : 'user');

      return isAdminTenant ? 'admin_tenant' : 'user';
    } catch (error) {
      console.error('Error al obtener rol del usuario:', error);
      setUserRole('user'); // Por defecto, rol de usuario regular
      return 'user';
    }
  };

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }

      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para abrir el modal de detalles de transacción
  const handleTransactionClick = async (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
    setIsModalInitialLoad(true); // Marcar como carga inicial
    setActiveTab('general');
    setTransactionImages([]);
    setTransactionFiles([]);
    setCurrentImageIndex(0);
    setImageError('');
    setFullScreenImage(false);

    // Cargar imágenes de la transacción
    await fetchTransactionImages(transaction);

    // Después de un breve delay, quitar la marca de carga inicial
    setTimeout(() => setIsModalInitialLoad(false), 400);
  };

  // Función para cerrar el modal
  const closeTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
    setTransactionImages([]);
    setTransactionFiles([]);
    setImageError('');
    setFullScreenImage(false);
  };

  // Función mejorada para obtener las imágenes con caché
  const fetchTransactionImages = async (transaction) => {
    const transactionId = transaction.transaction_id || transaction.id;

    // Verificar si ya están en caché
    const cachedImages = getFromCache(transactionId);
    if (cachedImages) {
      console.log('🎯 Imágenes cargadas desde caché para transacción:', transactionId);
      setTransactionImages(cachedImages);
      setCurrentImageIndex(0);
      setImageError('');
      return;
    }

    setLoadingImages(true);
    setImageError('');

    try {
      console.log('🔄 Descargando imágenes para transacción:', transactionId);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'transactions_view_pending_transactions_images',
            tenant_id: tenantId,
            transaction_id: transactionId
          }
        }
      );

      if (response.status === 200) {
        if (response.data.pre_signed_urls) {
          const images = response.data.pre_signed_urls;

          // Guardar en caché
          addToCache(transactionId, images);

          setTransactionImages(images);
          setCurrentImageIndex(0);

          console.log('✅ Imágenes descargadas y guardadas en caché:', images.length, 'imágenes');
        } else {
          setImageError('No se encontraron imágenes para esta transacción.');
        }

        if (response.data.files_name && Array.isArray(response.data.files_name)) {
          setTransactionFiles(response.data.files_name);
          console.log('✅ Archivos adjuntos recibidos:', response.data.files_name.length);
        }
      } else {
        setImageError('No se encontraron imágenes para esta transacción.');
      }
    } catch (error) {
      console.error('Error al obtener imágenes de la transacción:', error);
      setImageError('Error al cargar las imágenes de la transacción.');
    } finally {
      setLoadingImages(false);
    }
  };

  // Funciones del sistema de caché
  const addToCache = (transactionId, images) => {
    const MAX_CACHE_SIZE = 10;

    setImageCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(transactionId, images);
      return newCache;
    });

    setCacheOrder(prevOrder => {
      const newOrder = prevOrder.filter(id => id !== transactionId);
      newOrder.push(transactionId);

      // Si excedemos el límite, eliminar las más antiguas
      if (newOrder.length > MAX_CACHE_SIZE) {
        const toRemove = newOrder.shift();
        setImageCache(prevCache => {
          const newCache = new Map(prevCache);
          newCache.delete(toRemove);
          return newCache;
        });
      }

      return newOrder;
    });
  };

  const getFromCache = (transactionId) => {
    return imageCache.get(transactionId);
  };

  const clearCache = () => {
    setImageCache(new Map());
    setCacheOrder([]);
  };

  // Limpiar caché cuando el usuario abandone la página
  useEffect(() => {
    return () => {
      clearCache();
    };
  }, []);

  // Manejo de eventos de teclado para navegación
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (fullScreenImage && transactionImages.length > 1) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevImage();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          nextImage();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeFullScreen();
        }
      }
    };

    if (fullScreenImage) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [fullScreenImage, transactionImages.length]);

  // Resetear posición y escala al cambiar imagen
  useEffect(() => {
    setImagePosition({ x: 0, y: 0 });
    setImageScale(1);
  }, [currentImageIndex]);

  // Navegación de imágenes mejorada (sin recargar modal)
  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (transactionImages.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === transactionImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (transactionImages.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? transactionImages.length - 1 : prev - 1
      );
    }
  };

  const goToImage = (index, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentImageIndex(index);
  };

  // Función mejorada para cerrar solo pantalla completa
  const closeFullScreen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFullScreenImage(false);
  };

  // Función para abrir pantalla completa
  const openFullScreen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFullScreenImage(true);
  };

  // Funciones para arrastre de imágenes
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Función para zoom con rueda del mouse
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.min(Math.max(1, imageScale + delta), 3);
    setImageScale(newScale);

    // Resetear posición si se hace zoom out completo
    if (newScale === 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  };

  // Función para descargar contrato
  const downloadContract = async () => {
    if (!selectedTransaction) return;

    setLoadingContract(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts',
        {
          headers,
          params: {
            transaction_id: selectedTransaction.transaction_id || selectedTransaction.id
          }
        }
      );

      if (response.status === 200 && response.data.presigned_url) {
        const pdfUrl = response.data.presigned_url;

        // Abrir PDF en nueva pestaña del navegador
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');

        console.log('✅ Contrato abierto en nueva pestaña del navegador');
      } else {
        throw new Error('No se pudo obtener el contrato');
      }
    } catch (error) {
      console.error('Error al descargar contrato:', error);
      alert('Error al obtener el contrato. Por favor, inténtalo de nuevo.');
    } finally {
      setLoadingContract(false);
    }
  };

  // Función para descargar un archivo adjunto
  const downloadFile = async (fileName) => {
    if (!selectedTransaction) return;

    setDownloadingFileName(fileName);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts',
        {
          headers,
          params: {
            transaction_id: selectedTransaction.transaction_id || selectedTransaction.id,
            mode: 'file',
            file_name: fileName
          }
        }
      );

      if (response.status === 200 && response.data.presigned_url) {
        window.open(response.data.presigned_url, '_blank', 'noopener,noreferrer');
        console.log('✅ Archivo adjunto abierto en nueva pestaña:', fileName);
      } else {
        throw new Error('No se pudo obtener el archivo');
      }
    } catch (error) {
      console.error('Error al descargar archivo adjunto:', error);
      alert('Error al obtener el archivo. Por favor, inténtalo de nuevo.');
    } finally {
      setDownloadingFileName(null);
    }
  };

  // Función para abrir el modal de cancelación
  const openCancelModal = () => {
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Función para cerrar el modal de cancelación
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelReason('');
    setCancelLoading(false);
  };

  // Función para cancelar la transacción
  const handleCancelTransaction = async () => {
    if (!selectedTransaction) {
      alert('No se pudo identificar la transacción.');
      return;
    }

    // Validar que la razón tenga más de 4 caracteres
    if (cancelReason.trim().length <= 4) {
      alert('El motivo de cancelación debe tener más de 4 caracteres.');
      return;
    }

    setCancelLoading(true);

    try {
      const headers = await getAuthHeaders();

      // Preparar los datos para la solicitud PATCH
      const data = {
        tradein_step: 'canceled_web',
        transaction_id: selectedTransaction.transaction_id || selectedTransaction.id,
        tenant_id: tenantId,
        retailer: selectedTransaction.retailer || '',
        cancelled_reason: cancelReason.trim()
      };

      console.log('📤 Enviando solicitud de cancelación:', data);

      // Realizar la solicitud PATCH
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        data,
        { headers }
      );

      // Manejar la respuesta
      if (response.status === 200 || response.status === 201 || response.status === 202 || response.status === 204) {
        setCancelSuccess(true);

        // Usar el mensaje de la API si está disponible
        if (response.data && response.data.message) {
          setCancelSuccessMessage(response.data.message);
        } else {
          setCancelSuccessMessage('Transacción cancelada correctamente.');
        }

        console.log('✅ Transacción cancelada exitosamente:', response.data);

        // Remover la transacción de las listas
        setAllTransactions(prev => prev.filter(t =>
          (t.transaction_id || t.id) !== (selectedTransaction.transaction_id || selectedTransaction.id)
        ));
        setFilteredTransactions(prev => prev.filter(t =>
          (t.transaction_id || t.id) !== (selectedTransaction.transaction_id || selectedTransaction.id)
        ));

        // Cerrar modales
        closeCancelModal();
        closeTransactionModal();
      } else {
        throw new Error(`La respuesta del servidor no fue exitosa: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error al cancelar la transacción:', error);

      // Intentar extraer un mensaje de error más detallado
      let errorMessage = 'Error al cancelar la transacción';

      if (error.response) {
        console.error('Respuesta de error de la API:', error.response.data);

        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage += `: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      alert(errorMessage);
    } finally {
      setCancelLoading(false);
    }
  };

  // Mostrar mensaje de éxito temporal para cancelación
  useEffect(() => {
    if (cancelSuccess) {
      const timer = setTimeout(() => {
        setCancelSuccess(false);
        setCancelSuccessMessage('');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [cancelSuccess]);

  // Configuración de pestañas y agrupación de campos
  const getTabConfig = () => {
    return [
      {
        id: 'general',
        label: 'General',
        icon: '📋',
        fields: ['transaction_id', 'timestamp', 'tradein_status', 'tradein_type', 'brand', 'retailer']
      },
      {
        id: 'dispositivo',
        label: 'Dispositivo',
        icon: '📱',
        fields: ['buyback_device_name_std', 'buyback_device_imei', 'buyback_device_imei_2', 'buyback_device_brand', 'buyback_device_storage', 'buyback_device_final_grade', 'buyback_device_cosmetic_grade', 'buyback_device_functional_grade', 'buyback_device_category']
      },
      {
        id: 'montos',
        label: 'Montos',
        icon: '💰',
        fields: ['bonus_amount_trader', 'bonus_amount_campaign', 'bonus_amount_total', 'bonus_amount_currency']
      },
      {
        id: 'cliente',
        label: 'Cliente',
        icon: '👤',
        fields: ['customer_customer_name', 'customer_customer_surname', 'customer_customer_id', 'customer_customer_email', 'customer_customer_address', 'customer_customer_city', 'customer_customer_department', 'customer_customer_phone']
      },
      {
        id: 'ubicacion',
        label: 'Ubicación',
        icon: '🏪',
        fields: ['store_name', 'store_city', 'company_name']
      },
      {
        id: 'promotor',
        label: 'Promotor',
        icon: '👨‍💼',
        fields: ['customer_advisor_id', 'customer_advisor_name', 'promoter_id', 'promoter_name', 'promoter_email']
      },
      {
        id: 'vendido',
        label: 'Vendido',
        icon: '📊',
        fields: ['sold_device_name', 'sold_device_storage', 'sold_device_price', 'sold_device_category']
      },
      {
        id: 'gestion',
        label: 'Retoma Manual',
        icon: '⚙️',
        fields: ['manual_form_reason', 'review_timestamp', 'reviewer_id', 'reviewer_name', 'rejection_reason', 'cancelled_by_id', 'cancelled_by_name', 'cancelled_by_rol', 'cancelled_date', 'cancelled_reason']
      }
    ];
  };

  // Obtener los campos de una pestaña específica que tienen datos
  const getTabFieldsWithData = (tabId, transaction) => {
    const tabConfig = getTabConfig().find(tab => tab.id === tabId);
    if (!tabConfig) return [];

    return tabConfig.fields.filter(fieldKey => {
      const value = transaction[fieldKey];
      return value !== undefined && value !== null && value !== '';
    });
  };

  // Cargar datos iniciales
  useEffect(() => {
    const initializeData = async () => {
      // Obtener rol del usuario
      const role = await getUserRole();

      // Establecer tipo de búsqueda por defecto según rol
      if (role === 'admin_tenant') {
        setSearchType('imei');
      } else {
        setSearchType('transaction');
      }

      // Obtener fechas por defecto (últimos 3 días)
      const dateRange = getDefaultDateRange();

      setFilters(prev => ({
        ...prev,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }));

      // Cargar todas las transacciones con fechas por defecto
      await fetchAllTransactions(dateRange.startDate, dateRange.endDate);
    };

    initializeData();
  }, []);

  // Actualizar los datos mostrados cuando cambien los filtrados o la página
  useEffect(() => {
    updateDisplayedTransactions(filteredTransactions, currentPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);



  // Función para convertir fecha YYYY-MM-DD a epoch en segundos
  const convertDateToEpoch = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00.000Z'); // Usar UTC para evitar problemas de zona horaria
    return Math.floor(date.getTime() / 1000);
  };

  // Función para cargar TODAS las transacciones finalizadas (como en DashboardEjecutivo)
  const fetchAllTransactions = async (startDate, endDate) => {
    setLoading(true);
    setError('');

    let params = {
      tenant_id: tenantId,
      mode: 'overview', // Obtener solo transacciones finalizadas
      returning_type: 'min' // ✅ NUEVO: Modo ligero para visualización
    };

    // Convertir fechas a Epoch (segundos) como en DashboardEjecutivo
    if (startDate) {
      params.start_date = convertDateToEpoch(startDate);
    }
    if (endDate) {
      const endDateObj = new Date(endDate);
      endDateObj.setUTCHours(23, 59, 59, 999); // Fin del día UTC
      params.end_date = Math.floor(endDateObj.getTime() / 1000);
    }

    try {
      console.log("🔄 Cargando TODAS las transacciones finalizadas con parámetros:", params);
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers: await getAuthHeaders(),
          params
        }
      );

      if (response.status === 200) {
        const transactionData = Array.isArray(response.data) ? response.data : [];

        // Ordenar por fecha (timestamp) de más reciente a más antigua
        const sortedData = transactionData.sort((a, b) => {
          const timestampA = a.timestamp || 0;
          const timestampB = b.timestamp || 0;
          return timestampB - timestampA; // Orden descendente (más reciente primero)
        });

        console.log("✅ Transacciones finalizadas cargadas y ordenadas:", sortedData.length);

        // Procesar listas únicas para filtros
        const retailersList = [...new Set(sortedData.map(t => t.retailer))].filter(Boolean).sort();
        const brandsList = [...new Set(sortedData.map(t => t.brand))].filter(Boolean).sort();
        const storesList = [...new Set(sortedData.map(t => t.store_name))].filter(Boolean).sort();

        setRetailers(retailersList);
        setBrands(brandsList);
        setStores(storesList);

        // Guardar todos los datos
        setAllTransactions(sortedData);

        // Inicialmente, los datos filtrados son todos los datos
        setFilteredTransactions(sortedData);

        // Reiniciar a la página 1
        setCurrentPage(1);

        setRetryCount(0);
      } else {
        setAllTransactions([]);
        setFilteredTransactions([]);
        setError('No se encontraron transacciones finalizadas en el período seleccionado.');
      }
    } catch (error) {
      console.error('❌ Error al obtener transacciones finalizadas:', error);
      setError('Error al cargar transacciones finalizadas. Inténtalo de nuevo.');
      if (retryCount < 3) {
        setRetryCount(prevCount => prevCount + 1);
        setTimeout(() => fetchAllTransactions(startDate, endDate), 1000);
      } else {
        setError('No se pudieron cargar las transacciones después de varios intentos.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros del lado cliente
  const applyClientSideFilters = (data, currentFilters) => {
    if (!data || data.length === 0) return [];

    return data.filter(transaction => {
      // Filtro por retailer
      if (currentFilters.retailer && transaction.retailer !== currentFilters.retailer) {
        return false;
      }

      // Filtro por marca
      if (currentFilters.brand && transaction.brand !== currentFilters.brand) {
        return false;
      }

      // Filtro por tienda
      if (currentFilters.store && transaction.store_name !== currentFilters.store) {
        return false;
      }

      return true;
    });
  };

  // ==================== FUNCIONES PARA EXPORTACIÓN CON CSV DESDE S3 ====================

  // Función para parsear CSV descargado desde S3
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length === 0) return [];

    const headers = [];
    let i = 0;
    let inQuotes = false;
    let currentField = '';

    // Parsear encabezados
    for (let char of lines[0]) {
      if (char === '"') {
        if (inQuotes && lines[0][i + 1] === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        headers.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
      i++;
    }
    headers.push(currentField.trim());

    // Parsear filas de datos
    const rows = [];
    for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      if (!line.trim()) continue;

      const values = [];
      currentField = '';
      inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            currentField += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(currentField);
          currentField = '';
        } else {
          currentField += char;
        }
      }
      values.push(currentField);

      // Crear objeto con los datos
      const rowObj = {};
      headers.forEach((header, index) => {
        rowObj[header] = values[index] || '';
      });
      rows.push(rowObj);
    }

    return rows;
  };

  // Función para obtener datos completos desde S3 para exportación
  const fetchTransactionDataForExport = async (startDate, endDate) => {
    try {
      console.log('📦 Solicitando datos completos para exportación desde S3...');

      let params = {
        tenant_id: tenantId,
        mode: 'overview',
        returning_type: 'complete' // ✅ NUEVO: Solicitar CSV completo desde S3
      };

      // Convertir fechas a Epoch (segundos)
      if (startDate) {
        params.start_date = convertDateToEpoch(startDate);
      }
      if (endDate) {
        const endDateObj = new Date(endDate);
        endDateObj.setUTCHours(23, 59, 59, 999);
        params.end_date = Math.floor(endDateObj.getTime() / 1000);
      }

      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers,
          params
        }
      );

      if (response.status === 200 && response.data.download_url) {
        const { download_url, total_items, expires_in } = response.data;
        console.log(`📦 CSV disponible para descarga (${total_items} registros, expira en ${expires_in}s)`);

        // Descargar el CSV desde S3
        console.log('⬇️ Descargando CSV desde S3...');
        const csvResponse = await axios.get(download_url);
        console.log('✅ CSV descargado exitosamente');

        // Parsear el CSV
        const parsedData = parseCSV(csvResponse.data);
        console.log(`✅ CSV parseado: ${parsedData.length} registros`);

        return parsedData;
      } else {
        throw new Error('No se pudo obtener el enlace de descarga del CSV');
      }
    } catch (error) {
      console.error('❌ Error al obtener datos completos para exportación:', error);
      throw error;
    }
  };

  // ==================== FIN FUNCIONES PARA EXPORTACIÓN ====================

  // Función para buscar transacciones por IMEI
  const searchByImei = async (imei) => {
    setLoading(true);
    setError('');

    const trimmedImei = imei.trim();
    if (!trimmedImei) {
      setError('Por favor ingresa un IMEI válido');
      setLoading(false);
      return;
    }

    try {
      console.log("🔍 Buscando transacciones por IMEI:", trimmedImei);
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers: await getAuthHeaders(),
          params: {
            tenant_id: tenantId,
            mode: 'by_imei',
            imei: trimmedImei
          }
        }
      );

      if (response.status === 200) {
        const transactionData = Array.isArray(response.data) ? response.data : [];

        // Ordenar por fecha (timestamp) de más reciente a más antigua
        const sortedData = transactionData.sort((a, b) => {
          const timestampA = a.timestamp || 0;
          const timestampB = b.timestamp || 0;
          return timestampB - timestampA;
        });

        console.log("✅ Transacciones por IMEI encontradas:", sortedData.length);

        // Actualizar todos los estados
        setAllTransactions(sortedData);
        setFilteredTransactions(sortedData);
        setCurrentPage(1);

        // Actualizar listas para filtros
        const retailersList = [...new Set(sortedData.map(t => t.retailer))].filter(Boolean).sort();
        const brandsList = [...new Set(sortedData.map(t => t.brand))].filter(Boolean).sort();
        const storesList = [...new Set(sortedData.map(t => t.store_name))].filter(Boolean).sort();

        setRetailers(retailersList);
        setBrands(brandsList);
        setStores(storesList);

        if (sortedData.length === 0) {
          setError(`No se encontraron transacciones para el IMEI: ${trimmedImei}`);
        }
      } else {
        setError(`No se encontraron transacciones para el IMEI: ${trimmedImei}`);
      }
    } catch (error) {
      console.error('❌ Error al buscar por IMEI:', error);
      setError('Error al buscar transacciones por IMEI. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Función para buscar transacción por ID
  const searchByTransactionId = async (transactionId) => {
    setLoading(true);
    setError('');

    const trimmedId = transactionId.trim();
    if (!trimmedId) {
      setError('Por favor ingresa un ID de transacción válido');
      setLoading(false);
      return;
    }

    try {
      console.log("🔍 Buscando transacción por ID:", trimmedId);
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers: await getAuthHeaders(),
          params: {
            tenant_id: tenantId,
            mode: 'unique',
            transaction_id: trimmedId
          }
        }
      );

      if (response.status === 200) {
        // El backend puede devolver un objeto individual o un array
        let transactionData = [];
        if (Array.isArray(response.data)) {
          transactionData = response.data;
        } else if (response.data && typeof response.data === 'object') {
          // Si es un objeto individual, convertirlo a array
          transactionData = [response.data];
        }

        console.log("✅ Respuesta del backend:", response.data);
        console.log("✅ Transacción procesada:", transactionData.length, "registro(s)");

        // Actualizar todos los estados
        setAllTransactions(transactionData);
        setFilteredTransactions(transactionData);
        setCurrentPage(1);

        // Actualizar listas para filtros
        const retailersList = [...new Set(transactionData.map(t => t.retailer))].filter(Boolean).sort();
        const brandsList = [...new Set(transactionData.map(t => t.brand))].filter(Boolean).sort();
        const storesList = [...new Set(transactionData.map(t => t.store_name))].filter(Boolean).sort();

        setRetailers(retailersList);
        setBrands(brandsList);
        setStores(storesList);

        if (transactionData.length === 0) {
          setError(`No se encontró la transacción con ID: ${trimmedId}`);
        }
      } else {
        setError(`No se encontró la transacción con ID: ${trimmedId}`);
      }
    } catch (error) {
      console.error('❌ Error al buscar por ID de transacción:', error);
      setError('Error al buscar la transacción. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Función para calcular los datos de la página actual
  const updateDisplayedTransactions = (filteredData, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageData = filteredData.slice(startIndex, endIndex);

    setDisplayedTransactions(pageData);
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
  };

  // Función para aplicar filtros de visualización
  const handleApplyViewFilters = () => {
    setLoading(true);
    try {
      // Aplicar filtros a los datos existentes
      const filtered = applyClientSideFilters(allTransactions, filters);
      setFilteredTransactions(filtered);
      setCurrentPage(1);

      console.log('✅ Filtros de visualización aplicados');
    } catch (error) {
      console.error('❌ Error al aplicar filtros de visualización:', error);
    } finally {
      setLoading(false);
    }

    // Auto-colapsar panel después de aplicar filtros
    setControlPanelCollapsed(true);
  };

  // Función para aplicar filtros de fecha (recarga datos)
  const handleApplyDateFilters = () => {
    // Validar que no sean más de 40 días
    if (!filters.startDate || !filters.endDate) {
      setError('Por favor selecciona fechas válidas');
      return;
    }

    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 40) {
      setError('❌ No es posible realizar búsquedas de más de 40 días. Por favor selecciona un período menor.');
      return;
    }

    if (startDate > endDate) {
      setError('❌ La fecha de inicio debe ser menor o igual a la fecha de fin.');
      return;
    }

    // Limpiar errores si todo está bien
    setError('');

    fetchAllTransactions(filters.startDate, filters.endDate);
    setControlPanelCollapsed(true);
  };

  // Función para limpiar filtros de visualización
  const handleClearViewFilters = () => {
    setFilters(prev => ({
      ...prev,
      retailer: '',
      brand: '',
      store: ''
    }));

    // Recargar datos originales completos
    setFilteredTransactions(allTransactions);
    setCurrentPage(1);

    console.log('✅ Filtros de visualización limpiados');
    setControlPanelCollapsed(true);
  };

  // Función para limpiar filtros de fecha
  const handleClearDateFilters = () => {
    const dateRange = getDefaultDateRange();
    setFilters(prev => ({
      ...prev,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    }));
  };

  // Función para exportar datos en Excel (AHORA DESCARGA DESDE S3)
  const handleExportExcel = async () => {
    setDownloadStatus('loading');
    setError('');

    try {
      console.log("📦 Iniciando exportación Excel desde CSV en S3...");

      // ✅ NUEVO: Obtener datos completos desde S3 en lugar de usar filteredTransactions
      const completeTransactionsData = await fetchTransactionDataForExport(
        filters.startDate,
        filters.endDate
      );

      if (!completeTransactionsData || completeTransactionsData.length === 0) {
        setError('No hay datos para exportar en el período seleccionado.');
        setDownloadStatus('idle');
        return;
      }

      console.log(`📊 Procesando ${completeTransactionsData.length} transacciones desde CSV...`);

      // Mapear todos los campos del CSV al Excel con nombres en español
      const transactionsForExcel = completeTransactionsData.map(t => {
        const row = {};

        // Iterar sobre FIELDS_MAPPING para crear las columnas en el orden correcto
        Object.keys(FIELDS_MAPPING).forEach(fieldKey => {
          const columnName = FIELDS_MAPPING[fieldKey].name;
          let value = t[fieldKey];

          // Campo calculado: TOTAL REFURBI (suma de bonus_amount_trader + bonus_amount_additional)
          if (fieldKey === 'total_refurbi') {
            const bonusTrader = t['bonus_amount_trader'];
            const bonusAdditional = t['bonus_amount_additional'];

            let traderValue = 0;
            let additionalValue = 0;

            // Convertir bonus_amount_trader a número
            if (bonusTrader !== undefined && bonusTrader !== null && bonusTrader !== '') {
              try {
                traderValue = typeof bonusTrader === 'string' ? parseFloat(bonusTrader.toString().replace(/,/g, '')) : Number(bonusTrader);
                if (isNaN(traderValue)) traderValue = 0;
              } catch (e) {
                traderValue = 0;
              }
            }

            // Convertir bonus_amount_additional a número
            if (bonusAdditional !== undefined && bonusAdditional !== null && bonusAdditional !== '') {
              try {
                additionalValue = typeof bonusAdditional === 'string' ? parseFloat(bonusAdditional.toString().replace(/,/g, '')) : Number(bonusAdditional);
                if (isNaN(additionalValue)) additionalValue = 0;
              } catch (e) {
                additionalValue = 0;
              }
            }

            // Calcular la suma
            row[columnName] = traderValue + additionalValue;
          }
          // Formatear fechas como objetos Date para Excel
          else if (fieldKey === 'timestamp' || fieldKey === 'review_timestamp' || fieldKey === 'cancelled_date') {
            if (value != null && value !== '' && !isNaN(Number(value))) {
              const epochSec = Number(value);
              const dateObj = new Date(epochSec * 1000);
              row[columnName] = dateObj;
            } else {
              row[columnName] = '';
            }
          }
          // Formatear campos numéricos/dinero
          else if (fieldKey === 'bonus_amount_campaign' || fieldKey === 'bonus_amount_trader' ||
            fieldKey === 'bonus_amount_total' || fieldKey === 'sold_device_price' || fieldKey === 'bonus_amount_additional') {
            if (value !== undefined && value !== null && value !== '') {
              try {
                let numValue = typeof value === 'string' ? parseFloat(value.toString().replace(/,/g, '')) : Number(value);
                row[columnName] = !isNaN(numValue) ? numValue : 0;
              } catch (e) {
                row[columnName] = 0;
              }
            } else {
              row[columnName] = 0;
            }
          }
          // Para otros campos, mantener como string
          else {
            row[columnName] = value !== undefined && value !== null ? String(value) : '';
          }
        });

        return row;
      });

      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(transactionsForExcel, {
        dateNF: 'dd/mm/yyyy hh:mm:ss'
      });

      // Ajustar ancho de columnas automáticamente
      const colWidths = [];
      Object.values(FIELDS_MAPPING).forEach((field, index) => {
        const columnName = field.name;
        const maxLength = Math.max(
          columnName.length,
          ...transactionsForExcel.slice(0, 100).map(row =>
            String(row[columnName] || '').length
          )
        );
        colWidths[index] = { width: Math.min(Math.max(maxLength + 2, 10), 50) };
      });
      worksheet['!cols'] = colWidths;

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transacciones Finalizadas');

      // Generar el nombre del archivo con fecha actual
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
      const fileName = `transacciones_finalizadas_completas_${dateStr}_${timeStr}.xlsx`;

      // Descargar el archivo con opciones para manejar fechas correctamente
      XLSX.writeFile(workbook, fileName, {
        cellDates: true,
        dateNF: 'dd/mm/yyyy hh:mm:ss'
      });

      setDownloadStatus('success');
      console.log(`✅ Excel generado exitosamente: ${fileName} (${completeTransactionsData.length} registros completos desde S3)`);

    } catch (e) {
      console.error('❌ Error al generar el Excel desde S3:', e);
      setError(`Error al generar el archivo Excel: ${e.message || 'Inténtalo de nuevo.'}`);
      setDownloadStatus('idle');
    }

    // Resetear estado 'success' después de un tiempo
    setTimeout(() => {
      if (downloadStatus === 'success') {
        setDownloadStatus('idle');
      }
    }, 3000);
  };

  // Función para exportar datos en CSV
  const handleExportCSV = () => {
    try {
      console.log("📥 Generando CSV con datos locales...");

      const dataToExport = filteredTransactions;

      if (!dataToExport || dataToExport.length === 0) {
        setError('No hay datos para exportar. Aplica filtros o carga transacciones primero.');
        return;
      }

      // Obtener solo los campos que queremos mostrar, ordenados
      const allKeys = new Set();
      dataToExport.forEach(transaction => {
        Object.keys(transaction).forEach(key => {
          if (ALLOWED_FIELDS.includes(key)) {
            allKeys.add(key);
          }
        });
      });

      const orderedFields = getOrderedFields(Array.from(allKeys));

      // Crear CSV
      const csvHeaders = orderedFields.map(field => formatColumnName(field));
      const csvRows = dataToExport.map(transaction => {
        return orderedFields.map(field => {
          let value = transaction[field];

          // Campo calculado: TOTAL REFURBI
          if (field === 'total_refurbi') {
            const bonusTrader = transaction['bonus_amount_trader'];
            const bonusAdditional = transaction['bonus_amount_additional'];

            let traderValue = 0;
            let additionalValue = 0;

            if (bonusTrader !== undefined && bonusTrader !== null && bonusTrader !== '') {
              try {
                traderValue = typeof bonusTrader === 'string' ? parseFloat(bonusTrader.toString().replace(/,/g, '')) : Number(bonusTrader);
                if (isNaN(traderValue)) traderValue = 0;
              } catch (e) {
                traderValue = 0;
              }
            }

            if (bonusAdditional !== undefined && bonusAdditional !== null && bonusAdditional !== '') {
              try {
                additionalValue = typeof bonusAdditional === 'string' ? parseFloat(bonusAdditional.toString().replace(/,/g, '')) : Number(bonusAdditional);
                if (isNaN(additionalValue)) additionalValue = 0;
              } catch (e) {
                additionalValue = 0;
              }
            }

            value = traderValue + additionalValue;
          }
          // Formatear fechas para CSV
          else if (field === 'timestamp' || field === 'review_timestamp' || field === 'cancelled_date') {
            value = formatDate(value);
          }

          // Escapar comillas dobles y envolver en comillas si contiene comas
          const stringValue = String(value || '');
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',');
      });

      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');

      // Crear blob y descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
      const fileName = `transacciones_finalizadas_${dateStr}_${timeStr}.csv`;

      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ CSV generado exitosamente: ${fileName} (${dataToExport.length} registros)`);

    } catch (e) {
      console.error('❌ Error al generar el CSV:', e);
      setError(`Error al generar el archivo CSV: ${e.message || 'Inténtalo de nuevo.'}`);
    }
  };

  // Función para exportar datos en PDF
  const handleExportPDF = () => {
    alert('Funcionalidad de exportación a PDF próximamente disponible');
  };

  // Handlers para navegación de páginas
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      updateDisplayedTransactions(filteredTransactions, newPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      updateDisplayedTransactions(filteredTransactions, newPage);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      updateDisplayedTransactions(filteredTransactions, page);
    }
  };

  // Función para formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';

    try {
      // Si es un número, lo tratamos como epoch
      if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
        const epoch = Number(timestamp);
        // Verificar si es en segundos (10 dígitos) o milisegundos (13 dígitos)
        const milliseconds = epoch < 10000000000 ? epoch * 1000 : epoch;

        // Crear la fecha usando UTC para evitar problemas de zona horaria
        const date = new Date(milliseconds);

        if (isNaN(date.getTime())) {
          return '-';
        }

        // Formatear usando los métodos UTC
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // +1 porque los meses comienzan en 0
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      }

      // Si es string, intentamos parsearlo como fecha normal
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '-';
      }

      // Formatear usando los métodos UTC
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} UTC`;
    } catch (e) {
      console.error('Error al formatear fecha:', e);
      return '-';
    }
  };

  // Función para renderizar las columnas de la tabla dinámicamente
  const renderTableHeaders = () => {
    if (displayedTransactions.length === 0) return null;

    // Obtener solo las claves permitidas de todas las transacciones mostradas
    const allKeys = new Set();
    displayedTransactions.forEach(transaction => {
      Object.keys(transaction).forEach(key => {
        if (ALLOWED_FIELDS.includes(key)) {
          allKeys.add(key);
        }
      });
    });

    // Convertir a array y ordenar usando el nuevo sistema de orden
    const headers = getOrderedFields(Array.from(allKeys));

    return (
      <tr>
        {headers.map(header => (
          <th key={header}>{formatColumnName(header)}</th>
        ))}
      </tr>
    );
  };

  // Función para formatear nombres de columnas
  const formatColumnName = (name) => {
    // Usar el mapeo de campos para obtener el nombre en español
    return FIELDS_MAPPING[name]?.name || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Función para renderizar las filas de la tabla dinámicamente
  const renderTableRows = () => {
    if (displayedTransactions.length === 0) return (
      <tr>
        <td colSpan="100%" className="no-results">No se encontraron transacciones finalizadas</td>
      </tr>
    );

    // Obtener solo las claves permitidas
    const allKeys = new Set();
    displayedTransactions.forEach(transaction => {
      Object.keys(transaction).forEach(key => {
        if (ALLOWED_FIELDS.includes(key)) {
          allKeys.add(key);
        }
      });
    });

    // Convertir a array y ordenar usando el nuevo sistema de orden
    const keys = getOrderedFields(Array.from(allKeys));

    return displayedTransactions.map((transaction, index) => (
      <tr
        key={index}
        className="transaction-row clickable-row"
        onClick={() => handleTransactionClick(transaction)}
        title="Haz clic para ver detalles completos"
      >
        {keys.map(key => (
          <td key={`${index}-${key}`}>
            {key === 'timestamp' || key === 'review_timestamp' || key === 'cancelled_date' ? formatDate(transaction[key]) :
              transaction[key] !== undefined ? String(transaction[key]) : '-'}
          </td>
        ))}
      </tr>
    ));
  };

  // Componente del Modal de Detalles de Transacción
  const TransactionDetailModal = () => {
    if (!showTransactionModal || !selectedTransaction) return null;

    const tabsConfig = getTabConfig();

    return (
      <div className={`transaction-modal-overlay ${isModalInitialLoad ? 'initial-load' : ''}`} onClick={closeTransactionModal}>
        <div className="transaction-modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header del Modal */}
          <div className="transaction-modal-header">
            <div className="transaction-modal-title">
              <h2>Detalles de Transacción</h2>
              <span className="transaction-id-badge">
                ID: {selectedTransaction.transaction_id}
              </span>
            </div>
            <div className="transaction-modal-actions">
              <button
                className="contract-download-btn"
                onClick={downloadContract}
                disabled={loadingContract}
                title="Ver contrato en nueva pestaña"
              >
                {loadingContract ? <FaSpinner className="spin" /> : <FaDownload />}
                <span>Descargar Contrato</span>
              </button>
              {/* Solo mostrar botón de cancelación si el estado es exactamente "finalizada" */}
              {selectedTransaction.tradein_status === 'finalizada' && (
                <button
                  className="cancel-transaction-btn"
                  onClick={openCancelModal}
                  title="Cancelar esta transacción"
                >
                  <FaTimes />
                  <span>Cancelar Transacción</span>
                </button>
              )}
              <button
                className="transaction-modal-close"
                onClick={closeTransactionModal}
                title="Cerrar"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Contenido Principal - Layout de 2 columnas */}
          <div className="transaction-modal-content">
            {/* Columna Izquierda - Galería de Imágenes (Siempre Visible) */}
            <div className="transaction-images-section">
              <div className="images-header">
                <h3><FaImage /> Imágenes de la Transacción</h3>
                {transactionImages.length > 0 && (
                  <span className="images-counter">
                    {currentImageIndex + 1} de {transactionImages.length}
                  </span>
                )}
              </div>

              <div className="images-gallery">
                {loadingImages ? (
                  <div className="images-loading">
                    <FaSpinner className="spin" />
                    <p>Cargando imágenes...</p>
                  </div>
                ) : imageError ? (
                  <div className="images-error">
                    <FaFileImage />
                    <p>{imageError}</p>
                  </div>
                ) : transactionImages.length > 0 ? (
                  <>
                    {/* Imagen Principal */}
                    <div className="main-image-container">
                      <img
                        src={transactionImages[currentImageIndex]}
                        alt={`Imagen ${currentImageIndex + 1} de la transacción`}
                        className="main-image"
                        onClick={openFullScreen}
                      />
                      <button
                        className="fullscreen-btn"
                        onClick={openFullScreen}
                        title="Ver en pantalla completa"
                      >
                        <FaExpand />
                      </button>

                      {/* Controles de navegación */}
                      {transactionImages.length > 1 && (
                        <>
                          <button
                            className="nav-btn nav-prev"
                            onClick={prevImage}
                            title="Imagen anterior"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            className="nav-btn nav-next"
                            onClick={nextImage}
                            title="Siguiente imagen"
                          >
                            <FaChevronRight />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Miniaturas */}
                    {transactionImages.length > 1 && (
                      <div className="thumbnails-container">
                        {transactionImages.map((imageUrl, index) => (
                          <img
                            key={index}
                            src={imageUrl}
                            alt={`Miniatura ${index + 1}`}
                            className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={(e) => goToImage(index, e)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-images">
                    <FaFileImage />
                    <p>No hay imágenes disponibles para esta transacción</p>
                  </div>
                )}
              </div>
            </div>

            {/* Columna Derecha - Pestañas de Datos */}
            <div className="transaction-data-section">
              {/* Navegación de Pestañas */}
              <div className="tabs-navigation">
                {tabsConfig.map(tab => {
                  const fieldsWithData = getTabFieldsWithData(tab.id, selectedTransaction);
                  const hasData = fieldsWithData.length > 0;

                  return (
                    <button
                      key={tab.id}
                      className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${!hasData ? 'empty' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                      disabled={!hasData}
                      title={hasData ? `${tab.label} (${fieldsWithData.length} campos)` : `${tab.label} (sin datos)`}
                    >
                      <span className="tab-icon">{tab.icon}</span>
                      <span className="tab-label">{tab.label}</span>
                      {hasData && <span className="data-count">{fieldsWithData.length}</span>}
                    </button>
                  );
                })}
                {/* Tab especial de Archivos Adjuntos */}
                <button
                  className={`tab-btn ${activeTab === 'archivos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('archivos')}
                  title={transactionFiles.length > 0 ? `Archivos adjuntos (${transactionFiles.length})` : 'Archivos adjuntos'}
                >
                  <span className="tab-icon"><FaPaperclip /></span>
                  <span className="tab-label">Archivos</span>
                  {transactionFiles.length > 0 && <span className="data-count">{transactionFiles.length}</span>}
                </button>
              </div>

              {/* Contenido de la Pestaña Activa */}
              <div className="tab-content">
                {(() => {
                  // Contenido especial para el tab de Archivos Adjuntos
                  if (activeTab === 'archivos') {
                    return (
                      <div className="fields-container">
                        <h3 className="tab-title">
                          <FaPaperclip /> Archivos Adjuntos
                        </h3>
                        {loadingImages ? (
                          <div className="no-data">
                            <FaSpinner className="spin" />
                            <p>Cargando archivos...</p>
                          </div>
                        ) : transactionFiles.length === 0 ? (
                          <div className="no-data">
                            <FaFile style={{ fontSize: '2rem', color: '#9ca3af', marginBottom: '8px' }} />
                            <p>No hay archivos adjuntos para esta transacción</p>
                          </div>
                        ) : (
                          <div className="files-list">
                            {transactionFiles.map((fileName, index) => (
                              <div key={index} className="file-item">
                                <div className="file-item-info">
                                  <FaFile className="file-item-icon" />
                                  <span className="file-item-name">{fileName}</span>
                                </div>
                                <button
                                  className="file-download-btn"
                                  onClick={() => downloadFile(fileName)}
                                  disabled={downloadingFileName === fileName}
                                  title={`Descargar ${fileName}`}
                                >
                                  {downloadingFileName === fileName
                                    ? <FaSpinner className="spin" />
                                    : <FaDownload />
                                  }
                                  <span>{downloadingFileName === fileName ? 'Descargando...' : 'Descargar'}</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const activeTabConfig = tabsConfig.find(tab => tab.id === activeTab);
                  const fieldsWithData = getTabFieldsWithData(activeTab, selectedTransaction);

                  if (!activeTabConfig || fieldsWithData.length === 0) {
                    return (
                      <div className="no-data">
                        <p>No hay datos disponibles en esta sección</p>
                      </div>
                    );
                  }

                  return (
                    <div className="fields-container">
                      <h3 className="tab-title">
                        {activeTabConfig.icon} {activeTabConfig.label}
                      </h3>
                      <div className="fields-grid">
                        {fieldsWithData.map(fieldKey => {
                          const fieldMapping = FIELDS_MAPPING[fieldKey];
                          const fieldLabel = fieldMapping ? fieldMapping.name : fieldKey;
                          let fieldValue = selectedTransaction[fieldKey];

                          // Formatear valor según el tipo de campo
                          if (fieldKey === 'timestamp' || fieldKey === 'review_timestamp' || fieldKey === 'cancelled_date') {
                            fieldValue = formatDate(fieldValue);
                          } else if (typeof fieldValue === 'object' && fieldValue !== null) {
                            fieldValue = JSON.stringify(fieldValue);
                          } else {
                            fieldValue = String(fieldValue);
                          }

                          return (
                            <div key={fieldKey} className="field-item">
                              <label className="field-label">{fieldLabel}</label>
                              <div className="field-value">{fieldValue}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Modal de Pantalla Completa para Imágenes */}
        {fullScreenImage && transactionImages.length > 0 && (
          <div
            className="fullscreen-overlay"
            onClick={closeFullScreen}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <div className="fullscreen-container" onClick={(e) => e.stopPropagation()}>
              <button
                className="fullscreen-close"
                onClick={closeFullScreen}
                title="Cerrar pantalla completa (Esc)"
              >
                <FaCompress />
              </button>
              <div className="fullscreen-image-wrapper">
                <img
                  src={transactionImages[currentImageIndex]}
                  alt={`Imagen ${currentImageIndex + 1} en pantalla completa`}
                  className="fullscreen-image draggable"
                  style={{
                    transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageScale})`,
                    cursor: isDragging ? 'grabbing' : imageScale > 1 ? 'grab' : 'pointer'
                  }}
                  onClick={imageScale > 1 ? undefined : closeFullScreen}
                  onMouseDown={handleMouseDown}
                  onWheel={handleWheel}
                  draggable={false}
                />
              </div>
              {transactionImages.length > 1 && (
                <>
                  <button
                    className="fullscreen-nav fullscreen-prev"
                    onClick={prevImage}
                    title="Imagen anterior (←)"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="fullscreen-nav fullscreen-next"
                    onClick={nextImage}
                    title="Siguiente imagen (→)"
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}
              <div className="fullscreen-counter">
                {currentImageIndex + 1} de {transactionImages.length}
                {imageScale > 1 && (
                  <span className="zoom-indicator"> • Zoom: {Math.round(imageScale * 100)}%</span>
                )}
              </div>
              <div className="fullscreen-instructions">
                <div>🖱️ Rueda: Zoom • ↔️ Arrastrar: Mover</div>
                <div>⌨️ ← → : Navegar • Esc: Cerrar</div>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  };


  // --- Renderizado --- 
  return (
    <div className="transacciones-container">
      <header className="header">
        <Logo
          variant="horizontal"
          size="medium"
          onClick={() => navigate('/dashboard')}
        />
        <div className="header-title">Transacciones Finalizadas</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>

      <div className="container">
        <div className="card animate-fade-in">
          {/* Nuevo contenedor para título y botón */}
          <div className="card-header-actions">
            <h2 className="card-title">Transacciones Finalizadas</h2>
            {/* Mover la sección de descarga aquí */}
            <div className="download-section">
              <button
                className="btn btn-primary"
                onClick={handleExportExcel}
                disabled={downloadStatus === 'loading'}
              >
                {downloadStatus === 'loading' && <FaSpinner className="spin" />}
                {downloadStatus === 'success' && <FaCheckCircle />}
                {downloadStatus === 'idle' && <FaDownload />}
                <span style={{ marginLeft: '8px' }}>
                  {downloadStatus === 'loading'
                    ? 'Creando reporte...'
                    : downloadStatus === 'success'
                      ? 'Reporte creado'
                      : 'Descargar transacciones'}
                </span>
              </button>
            </div>
          </div>

          {/* Panel de Control Colapsable - Similar a DashboardEjecutivo */}
          <div className={`unified-controls-section card unified-controls-sticky ${controlPanelCollapsed ? 'collapsed' : 'expanded'}`}>
            <div
              className="unified-controls-header"
              onClick={controlPanelCollapsed ? () => setControlPanelCollapsed(false) : () => setControlPanelCollapsed(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className="unified-controls-header-content">
                <h3 className="unified-controls-title">⚙️ Panel de Control</h3>
                <div className="unified-controls-subtitle">
                  {controlPanelCollapsed ?
                    'Haz clic para acceder a filtros, consultas y exportación' :
                    'Haz clic para colapsar el panel de control'
                  }
                </div>
              </div>
              <button
                className="unified-controls-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setControlPanelCollapsed(!controlPanelCollapsed);
                }}
                title={controlPanelCollapsed ? 'Expandir panel de control' : 'Colapsar panel de control'}
              >
                {controlPanelCollapsed ? '⚙️' : '⬆️'}
              </button>
            </div>

            <div className={`unified-controls-content ${controlPanelCollapsed ? 'hidden' : 'visible'}`}>
              <div className="controls-grid">
                {/* Sección 1: Período de Consulta */}
                <div className="control-section date-section">
                  <div className="section-header">
                    <h4 className="section-title">
                      📅 Período de Consulta
                      <button
                        className="info-tooltip-btn"
                        title="Selecciona el rango de fechas para obtener datos de la base de datos"
                      >
                        ❓
                      </button>
                    </h4>
                  </div>
                  <div className="section-content">
                    <div className="date-controls-vertical">
                      <div className="filter-group">
                        <label className="form-label">Fecha Inicio</label>
                        <input
                          type="date"
                          className="form-input"
                          value={filters.startDate}
                          onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        />
                      </div>

                      <div className="filter-group">
                        <label className="form-label">Fecha Fin</label>
                        <input
                          type="date"
                          className="form-input"
                          value={filters.endDate}
                          onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        />
                      </div>

                      <div className="date-actions">
                        <button
                          className="btn btn-primary btn-full-width"
                          onClick={handleApplyDateFilters}
                          disabled={loading || !filters.startDate || !filters.endDate}
                        >
                          {loading ? '⏳ Cargando...' : '🔄 Obtener Datos'}
                        </button>
                        <button
                          className="btn btn-outline btn-full-width"
                          onClick={handleClearDateFilters}
                        >
                          🗑️ Fechas por Defecto
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sección 2: Filtros de Visualización */}
                <div className="control-section filters-section">
                  <div className="section-header">
                    <h4 className="section-title">
                      🔍 Filtros de Visualización
                      <button
                        className="info-tooltip-btn"
                        title="Filtra los datos sin realizar nuevas consultas a la base de datos"
                      >
                        ❓
                      </button>
                    </h4>
                  </div>
                  <div className="section-content">
                    <div className="filters-controls-vertical">
                      <div className="filter-group">
                        <label className="form-label">Retailer</label>
                        <select
                          className="form-select"
                          value={filters.retailer}
                          onChange={(e) => setFilters({ ...filters, retailer: e.target.value })}
                        >
                          <option value="">Todos los retailers</option>
                          {retailers.map(retailer => (
                            <option key={retailer} value={retailer}>{retailer}</option>
                          ))}
                        </select>
                      </div>

                      <div className="filter-group">
                        <label className="form-label">Marca</label>
                        <select
                          className="form-select"
                          value={filters.brand}
                          onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
                        >
                          <option value="">Todas las marcas</option>
                          {brands.map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                      </div>

                      <div className="filter-group">
                        <label className="form-label">Tienda</label>
                        <select
                          className="form-select"
                          value={filters.store}
                          onChange={(e) => setFilters({ ...filters, store: e.target.value })}
                        >
                          <option value="">Todas las tiendas</option>
                          {stores.map(store => (
                            <option key={store} value={store}>{store}</option>
                          ))}
                        </select>
                      </div>

                      <div className="filters-actions">
                        <button
                          className="btn btn-primary btn-full-width"
                          onClick={handleApplyViewFilters}
                          disabled={loading}
                        >
                          {loading ? '⏳ Aplicando...' : '🔎 Aplicar Filtros'}
                        </button>
                        <button
                          className="btn btn-outline btn-full-width"
                          onClick={handleClearViewFilters}
                        >
                          🗑️ Limpiar Filtros
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sección 3: Exportación */}
                <div className="control-section export-section">
                  <div className="section-header">
                    <h4 className="section-title">
                      📊 Exportación de Datos
                      <button
                        className="info-tooltip-btn"
                        title="Descarga los datos filtrados en diferentes formatos"
                      >
                        ❓
                      </button>
                    </h4>
                  </div>
                  <div className="section-content">
                    <div className="export-controls-vertical">
                      <button
                        className="btn btn-secondary btn-export btn-full-width"
                        onClick={handleExportExcel}
                        disabled={downloadStatus === 'loading'}
                      >
                        {downloadStatus === 'loading' && <FaSpinner className="spin" />}
                        {downloadStatus === 'success' && <FaCheckCircle />}
                        {downloadStatus === 'idle' && <FaDownload />}
                        📊 Exportar Excel
                      </button>
                      <button
                        className="btn btn-accent btn-export btn-full-width"
                        onClick={handleExportCSV}
                      >
                        📄 Exportar CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sección de Búsquedas */}
          <div className={`unified-controls-section card ${searchSectionCollapsed ? 'collapsed' : 'expanded'}`}>
            <div
              className="unified-controls-header"
              onClick={searchSectionCollapsed ? () => setSearchSectionCollapsed(false) : () => setSearchSectionCollapsed(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className="unified-controls-header-content">
                <h3 className="unified-controls-title">🔍 Búsquedas Especializadas</h3>
                <div className="unified-controls-subtitle">
                  {searchSectionCollapsed ?
                    'Haz clic para realizar búsquedas por período, IMEI o transacción' :
                    'Haz clic para colapsar la sección de búsquedas'
                  }
                </div>
              </div>
              <button
                className="unified-controls-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchSectionCollapsed(!searchSectionCollapsed);
                }}
                title={searchSectionCollapsed ? 'Expandir búsquedas' : 'Colapsar búsquedas'}
              >
                {searchSectionCollapsed ? '🔍' : '⬆️'}
              </button>
            </div>

            <div className={`unified-controls-content ${searchSectionCollapsed ? 'hidden' : 'visible'}`}>
              <div className="search-controls-grid">
                <div className="search-type-selector">
                  <label className="form-label">Tipo de Búsqueda</label>
                  <select
                    className="form-select"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    {userRole === 'admin_tenant' && (
                      <>
                        <option value="imei">Por IMEI</option>
                        <option value="transaction">Por ID de Transacción</option>
                      </>
                    )}
                    {userRole !== 'admin_tenant' && (
                      <option value="transaction">Por ID de Transacción</option>
                    )}
                  </select>
                </div>

                <div className="search-controls">
                  {searchType === 'imei' && userRole === 'admin_tenant' && (
                    <div className="search-imei-controls">
                      <label className="form-label">Ingresa el IMEI</label>
                      <div className="search-input-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Ingresa el IMEI..."
                          value={searchImei}
                          onChange={(e) => setSearchImei(e.target.value)}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={() => searchByImei(searchImei)}
                          disabled={!searchImei.trim() || loading}
                        >
                          {loading ? '⏳ Buscando...' : '🔍 Buscar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {searchType === 'transaction' && (
                    <div className="search-transaction-controls">
                      <label className="form-label">Ingresa el ID de Transacción</label>
                      <div className="search-input-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Ingresa el ID de transacción..."
                          value={searchTransactionId}
                          onChange={(e) => setSearchTransactionId(e.target.value)}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={() => searchByTransactionId(searchTransactionId)}
                          disabled={!searchTransactionId.trim() || loading}
                        >
                          {loading ? '⏳ Buscando...' : '🔍 Buscar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* --- Tabla y Paginación --- */}
          <div style={{ marginTop: '2rem' }}> {/* Añadir espacio antes de la tabla */}
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Cargando transacciones finalizadas...</p>
              </div>
            ) : error ? (
              <div className="message message-error">{error}</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="transactions-table">
                    <thead>
                      {renderTableHeaders()}
                    </thead>
                    <tbody>
                      {renderTableRows()}
                    </tbody>
                  </table>
                </div>
                {/* Información de paginación y controles */}
                <div className="pagination-info">
                  <div className="pagination-summary">
                    Mostrando {displayedTransactions.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} de {filteredTransactions.length} transacciones
                    {allTransactions.length !== filteredTransactions.length && (
                      <span className="filter-indicator"> (filtradas de {allTransactions.length} totales)</span>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <button
                        className="btn btn-pagination"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        ← Anterior
                      </button>

                      <div className="pagination-pages">
                        {/* Mostrar números de página */}
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              className={`btn btn-page ${currentPage === pageNum ? 'active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        className="btn btn-pagination"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente →
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="form-actions">
            <button
              className="btn btn-outline back-button"
              onClick={() => navigate('/dashboard')}
            >
              Regresar al Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Detalles de Transacción */}
      <TransactionDetailModal />

      {/* Notificación de éxito para cancelación */}
      {cancelSuccess && (
        <div className="cancel-success-notification">
          <div className="success-icon">✓</div>
          <p>{cancelSuccessMessage}</p>
        </div>
      )}

      {/* Modal de Confirmación de Cancelación */}
      {showCancelModal && (
        <div className="cancel-modal-overlay" onClick={closeCancelModal}>
          <div className="cancel-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-modal-header">
              <h3>¿Cancelar esta transacción?</h3>
            </div>
            <div className="cancel-modal-body">
              <p>Esta acción cancelará permanentemente la transacción. Por favor, especifica el motivo de la cancelación.</p>

              <div className="cancel-reason-input-container">
                <label htmlFor="cancel-reason" className="cancel-reason-label">
                  Motivo de la cancelación: <span className="required-indicator">*</span>
                </label>
                <textarea
                  id="cancel-reason"
                  className="cancel-reason-textarea"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Escribe aquí el motivo de la cancelación (más de 4 caracteres)..."
                  rows={4}
                  autoFocus
                />
                {cancelReason.trim().length > 0 && cancelReason.trim().length <= 4 && (
                  <p className="cancel-reason-error">El motivo debe tener más de 4 caracteres</p>
                )}
              </div>
            </div>
            <div className="cancel-modal-actions">
              <button
                className="btn btn-outline"
                onClick={closeCancelModal}
                disabled={cancelLoading}
              >
                Volver
              </button>
              <button
                className="btn btn-danger-cancel"
                onClick={handleCancelTransaction}
                disabled={cancelLoading || cancelReason.trim().length <= 4}
              >
                {cancelLoading ? <FaSpinner className="spin" /> : <FaTimes />}
                <span>{cancelLoading ? 'Cancelando...' : 'Confirmar Cancelación'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Estilos JSX --- */}
      <style jsx>{`
          /* Estilos del Panel de Control - Copiados del DashboardEjecutivo.js */
          .unified-controls-section {
            background: white;
            border: 1px solid #e5e7eb;
            margin-bottom: 1.5rem;
            transition: all 0.3s ease;
            border-radius: 8px;
            overflow: hidden;
          }
          
          .unified-controls-sticky {
            position: sticky;
            top: 80px;
            z-index: 100;
          }
          
          .unified-controls-section.collapsed {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .unified-controls-section.expanded {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }
          
          .unified-controls-header {
            padding: 1.25rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #e5e7eb;
            transition: all 0.3s ease;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .unified-controls-section.collapsed .unified-controls-header {
            border-bottom: none;
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            color: #374151;
          }
          
          .unified-controls-section.collapsed .unified-controls-header:hover {
            background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
          }
          
          .unified-controls-header-content {
            flex: 1;
          }
          
          .unified-controls-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin: 0;
          }
          
          .unified-controls-subtitle {
            font-size: 0.9rem;
            opacity: 0.8;
            margin-top: 0.25rem;
          }
          
          .unified-controls-toggle-btn {
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            font-size: 1.2rem;
            cursor: pointer;
            padding: 0.5rem;
            border-radius: 6px;
            transition: all 0.2s ease;
          }
          
          .unified-controls-section.collapsed .unified-controls-toggle-btn {
            background: rgba(0, 0, 0, 0.05);
            border: 1px solid rgba(0, 0, 0, 0.1);
            color: #374151;
          }
          
          .unified-controls-toggle-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.05);
          }
          
          .unified-controls-section.collapsed .unified-controls-toggle-btn:hover {
            background: rgba(0, 0, 0, 0.1);
          }
          
          .unified-controls-content {
            transition: all 0.4s ease;
            overflow: hidden;
          }
          
          .unified-controls-content.visible {
            max-height: 800px;
            opacity: 1;
            padding: 1.5rem;
          }
          
          .unified-controls-content.hidden {
            max-height: 0;
            opacity: 0;
            padding: 0 1.5rem;
          }
          
          /* Grid horizontal para las tres secciones */
          .controls-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1.5rem;
            align-items: start;
          }
          
          /* Estilos para las secciones internas */
          .control-section {
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          
          .section-header {
            margin-bottom: 1rem;
            flex-shrink: 0;
          }
          
          .section-title {
            font-size: 1rem;
            font-weight: 600;
            margin: 0;
            color: #374151;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .info-tooltip-btn {
            background: rgba(0, 0, 0, 0.1);
            border: none;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            font-size: 0.7rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            color: #6b7280;
          }
          
          .info-tooltip-btn:hover {
            background: rgba(0, 0, 0, 0.2);
            transform: scale(1.1);
          }
          
          .section-content {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          
          /* Sección de fechas */
          .date-section {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            padding: 1.25rem;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
          }
          
          .date-controls-vertical {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            height: 100%;
          }
          
          .date-actions {
            margin-top: auto;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          /* Sección de filtros */
          .filters-section {
            background: #fefefe;
            padding: 1.25rem;
            border-radius: 8px;
            border: 1px solid #f3f4f6;
          }
          
          .filters-controls-vertical {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            height: 100%;
          }
          
          .filters-actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-top: auto;
          }
          
          /* Sección de exportación */
          .export-section {
            background: linear-gradient(135deg, #fef7cd 0%, #fef3c7 100%);
            padding: 1.25rem;
            border-radius: 8px;
            border: 1px solid #fde68a;
          }
          
          .export-controls-vertical {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            height: 100%;
          }
          
          .btn-export {
            padding: 0.6rem 1.2rem;
            font-weight: 500;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: center;
          }
          
          .btn-export:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          .btn-full-width {
            width: 100%;
            justify-content: center;
          }

          /* Estilos para la sección de búsquedas */
          .search-controls-grid {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 2rem;
            align-items: start;
          }
          
          .search-type-selector {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .search-controls {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          
          .search-input-group {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }
          
          .search-input-group input {
            flex: 1;
          }
          


          /* Estilo para el contenedor del título y botón */
          .card-header-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
          
          .card-title {
             margin-bottom: 0;
          }

          /* Estilos para paginación del lado cliente */
          .pagination-info {
            margin-top: 1.5rem;
            padding: 1rem;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #dee2e6;
          }

          .pagination-summary {
            text-align: center;
            margin-bottom: 1rem;
            font-size: 0.9rem;
            color: #6c757d;
          }

          .filter-indicator {
            font-style: italic;
            color: #28a745;
          }

          .pagination-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
          }

          .btn-pagination {
            padding: 0.5rem 1rem;
            background: #007bff;
            color: white;
            border: 1px solid #007bff;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .btn-pagination:hover:not(:disabled) {
            background: #0056b3;
            border-color: #0056b3;
          }

          .btn-pagination:disabled {
            background: #6c757d;
            border-color: #6c757d;
            cursor: not-allowed;
            opacity: 0.5;
          }

          .pagination-pages {
            display: flex;
            gap: 0.25rem;
          }

          .btn-page {
            padding: 0.5rem 0.75rem;
            background: white;
            color: #007bff;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 40px;
          }

          .btn-page:hover {
            background: #e9ecef;
            border-color: #adb5bd;
          }

          .btn-page.active {
            background: #007bff;
            color: white;
            border-color: #007bff;
          }

          .spin {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }

          /* Estilos para filas clickeables */
          .clickable-row {
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .clickable-row:hover {
            background-color: ${brandIdentity.colors.backgroundLight};
            transform: scale(1.001);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          /* ===== ESTILOS DEL MODAL DE DETALLES ===== */
          .transaction-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            padding: 20px;
            backdrop-filter: blur(4px);
          }

          .transaction-modal-container {
            background: ${brandIdentity.colors.white};
            border-radius: 16px;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
            max-width: 95vw;
            max-height: 95vh;
            width: 1400px;
            height: 800px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            opacity: 1;
            transform: scale(1) translateY(0);
          }

          .transaction-modal-overlay.initial-load .transaction-modal-container {
            animation: modalSlideIn 0.3s ease-out;
          }

          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          /* Header del Modal */
          .transaction-modal-header {
            background: linear-gradient(135deg, ${brandIdentity.colors.primary} 0%, ${brandIdentity.colors.primaryDark} 100%);
            color: ${brandIdentity.colors.white};
            padding: ${brandIdentity.spacing.lg};
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
          }

          .transaction-modal-actions {
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.md};
          }

          .contract-download-btn {
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: ${brandIdentity.colors.white};
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.md};
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.xs};
          }

          .contract-download-btn:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.2);
            transform: translateY(-1px);
          }

          .contract-download-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          /* Botón de cancelación de transacción */
          .cancel-transaction-btn {
            background: rgba(239, 68, 68, 0.9);
            border: 1px solid rgba(239, 68, 68, 1);
            color: ${brandIdentity.colors.white};
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.md};
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.xs};
          }

          .cancel-transaction-btn:hover {
            background: rgba(220, 38, 38, 0.95);
            border-color: rgba(220, 38, 38, 1);
            transform: translateY(-1px);
          }

          .cancel-transaction-btn:active {
            transform: translateY(0);
          }

          .transaction-modal-title h2 {
            margin: 0;
            font-size: ${brandIdentity.typography.sizes.h1};
            font-weight: ${brandIdentity.typography.weights.semibold};
            font-family: ${brandIdentity.typography.fontFamily};
          }

          .transaction-id-badge {
            background: rgba(255, 255, 255, 0.2);
            padding: ${brandIdentity.spacing.xs} ${brandIdentity.spacing.sm};
            border-radius: 20px;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
            margin-top: ${brandIdentity.spacing.xs};
            display: inline-block;
          }

          .transaction-modal-close {
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: ${brandIdentity.colors.white};
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm};
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: ${brandIdentity.typography.sizes.body};
          }

          .transaction-modal-close:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.05);
          }

          /* Contenido Principal */
          .transaction-modal-content {
            display: grid;
            grid-template-columns: 450px 1fr;
            gap: 0;
            flex: 1;
            overflow: hidden;
          }

          /* ===== SECCIÓN DE IMÁGENES (COLUMNA IZQUIERDA) ===== */
          .transaction-images-section {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border-right: 1px solid #e2e8f0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .images-header {
            padding: ${brandIdentity.spacing.lg};
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: ${brandIdentity.colors.white};
            flex-shrink: 0;
          }

          .images-header h3 {
            margin: 0;
            font-size: ${brandIdentity.typography.sizes.h2};
            font-weight: ${brandIdentity.typography.weights.semibold};
            color: ${brandIdentity.colors.textDark};
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.sm};
          }

          .images-counter {
            background: ${brandIdentity.colors.primary};
            color: ${brandIdentity.colors.white};
            padding: ${brandIdentity.spacing.xs} ${brandIdentity.spacing.sm};
            border-radius: 12px;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
          }

          .images-gallery {
            flex: 1;
            display: flex;
            flex-direction: column;
            padding: ${brandIdentity.spacing.lg};
            overflow: hidden;
          }

          .main-image-container {
            position: relative;
            flex: 1;
            background: ${brandIdentity.colors.white};
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            margin-bottom: ${brandIdentity.spacing.md};
          }

          .main-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            cursor: pointer;
            transition: transform 0.2s ease;
          }

          .main-image:hover {
            transform: scale(1.02);
          }

          .fullscreen-btn {
            position: absolute;
            top: ${brandIdentity.spacing.sm};
            right: ${brandIdentity.spacing.sm};
            background: rgba(0, 0, 0, 0.6);
            color: ${brandIdentity.colors.white};
            border: none;
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm};
            cursor: pointer;
            transition: all 0.2s ease;
            opacity: 0;
          }

          .main-image-container:hover .fullscreen-btn {
            opacity: 1;
          }

          .fullscreen-btn:hover {
            background: rgba(0, 0, 0, 0.8);
            transform: scale(1.1);
          }

          .nav-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(0, 0, 0, 0.6);
            color: ${brandIdentity.colors.white};
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
          }

          .main-image-container:hover .nav-btn {
            opacity: 1;
          }

          .nav-prev {
            left: ${brandIdentity.spacing.sm};
          }

          .nav-next {
            right: ${brandIdentity.spacing.sm};
          }

          .nav-btn:hover {
            background: rgba(0, 0, 0, 0.8);
            transform: translateY(-50%) scale(1.1);
          }

          .thumbnails-container {
            display: flex;
            gap: ${brandIdentity.spacing.sm};
            overflow-x: auto;
            padding: ${brandIdentity.spacing.sm} 0;
            max-height: 80px;
          }

          .thumbnail {
            width: 60px;
            height: 60px;
            object-fit: cover;
            border-radius: 8px;
            cursor: pointer;
            border: 2px solid transparent;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }

          .thumbnail:hover {
            border-color: ${brandIdentity.colors.primaryLight};
            transform: scale(1.05);
          }

          .thumbnail.active {
            border-color: ${brandIdentity.colors.primary};
            box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
          }

          .images-loading,
          .images-error,
          .no-images {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: ${brandIdentity.colors.textLight};
            text-align: center;
            padding: ${brandIdentity.spacing.xl};
          }

          .images-loading svg,
          .images-error svg,
          .no-images svg {
            font-size: 48px;
            margin-bottom: ${brandIdentity.spacing.md};
          }

          /* ===== SECCIÓN DE DATOS (COLUMNA DERECHA) ===== */
          .transaction-data-section {
            display: flex;
            flex-direction: column;
            overflow: hidden;
            background: ${brandIdentity.colors.white};
          }

          .tabs-navigation {
            display: flex;
            border-bottom: 1px solid #e2e8f0;
            background: ${brandIdentity.colors.backgroundLight};
            padding: ${brandIdentity.spacing.sm};
            gap: ${brandIdentity.spacing.xs};
            flex-wrap: wrap;
            overflow-x: auto;
            flex-shrink: 0;
          }

          .tab-btn {
            background: ${brandIdentity.colors.white};
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.md};
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.xs};
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
            white-space: nowrap;
            position: relative;
          }

          .tab-btn:hover:not(:disabled) {
            border-color: ${brandIdentity.colors.primaryLight};
            background: rgba(99, 102, 241, 0.05);
          }

          .tab-btn.active {
            background: ${brandIdentity.colors.primary};
            color: ${brandIdentity.colors.white};
            border-color: ${brandIdentity.colors.primaryDark};
          }

          .tab-btn.empty {
            opacity: 0.5;
            cursor: not-allowed;
            background: #f3f4f6;
          }

          .tab-icon {
            font-size: ${brandIdentity.typography.sizes.body};
          }

          .data-count {
            background: rgba(255, 255, 255, 0.2);
            color: ${brandIdentity.colors.white};
            border-radius: 10px;
            padding: 2px 6px;
            font-size: ${brandIdentity.typography.sizes.xs};
            font-weight: ${brandIdentity.typography.weights.semibold};
            margin-left: ${brandIdentity.spacing.xs};
          }

          .tab-btn:not(.active) .data-count {
            background: ${brandIdentity.colors.primary};
            color: ${brandIdentity.colors.white};
          }

          .tab-content {
            flex: 1;
            overflow-y: auto;
            padding: ${brandIdentity.spacing.lg};
          }

          .tab-title {
            margin: 0 0 ${brandIdentity.spacing.lg} 0;
            font-size: ${brandIdentity.typography.sizes.h2};
            font-weight: ${brandIdentity.typography.weights.semibold};
            color: ${brandIdentity.colors.textDark};
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.sm};
            padding-bottom: ${brandIdentity.spacing.md};
            border-bottom: 2px solid ${brandIdentity.colors.backgroundLight};
          }

          .fields-grid {
            display: grid;
            gap: ${brandIdentity.spacing.lg};
          }

          /* Estilos para la sección de archivos adjuntos */
          .files-list {
            display: flex;
            flex-direction: column;
            gap: ${brandIdentity.spacing.sm};
          }

          .file-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: ${brandIdentity.colors.backgroundLight};
            border-radius: 8px;
            padding: ${brandIdentity.spacing.md};
            transition: all 0.2s ease;
            gap: ${brandIdentity.spacing.md};
          }

          .file-item:hover {
            background: #e2e8f0;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .file-item-info {
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.sm};
            flex: 1;
            min-width: 0;
          }

          .file-item-icon {
            color: ${brandIdentity.colors.primary};
            flex-shrink: 0;
            font-size: 1.1rem;
          }

          .file-item-name {
            color: ${brandIdentity.colors.textDark};
            font-size: ${brandIdentity.typography.sizes.body};
            word-break: break-all;
            line-height: 1.4;
          }

          .file-download-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            background: ${brandIdentity.colors.primary};
            color: ${brandIdentity.colors.white};
            border: none;
            border-radius: 6px;
            padding: 7px 14px;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .file-download-btn:hover:not(:disabled) {
            background: ${brandIdentity.colors.primaryDark};
            transform: translateY(-1px);
          }

          .file-download-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          .field-item {
            background: ${brandIdentity.colors.backgroundLight};
            border-radius: 8px;
            padding: ${brandIdentity.spacing.md};
            transition: all 0.2s ease;
          }

          .field-item:hover {
            background: #e2e8f0;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .field-label {
            display: block;
            font-weight: ${brandIdentity.typography.weights.semibold};
            color: ${brandIdentity.colors.primary};
            font-size: ${brandIdentity.typography.sizes.small};
            margin-bottom: ${brandIdentity.spacing.xs};
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .field-value {
            color: ${brandIdentity.colors.textDark};
            font-size: ${brandIdentity.typography.sizes.body};
            font-weight: ${brandIdentity.typography.weights.regular};
            word-wrap: break-word;
            line-height: 1.5;
          }

          .no-data {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${brandIdentity.colors.textLight};
            font-style: italic;
          }

          /* ===== MODAL DE PANTALLA COMPLETA ===== */
          .fullscreen-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.95);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            cursor: pointer;
          }

          .fullscreen-container {
            position: relative;
            width: 95vw;
            height: 95vh;
            cursor: default;
            overflow: hidden;
          }

          .fullscreen-image-wrapper {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .fullscreen-image {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            transition: transform 0.1s ease-out;
            user-select: none;
          }

          .fullscreen-image.draggable {
            transition: none;
          }

          .fullscreen-close {
            position: absolute;
            top: -40px;
            right: 0;
            background: rgba(255, 255, 255, 0.1);
            color: ${brandIdentity.colors.white};
            border: none;
            border-radius: 8px;
            padding: ${brandIdentity.spacing.sm};
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .fullscreen-close:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          .fullscreen-nav {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.1);
            color: ${brandIdentity.colors.white};
            border: none;
            border-radius: 50%;
            width: 50px;
            height: 50px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${brandIdentity.typography.sizes.h2};
          }

          .fullscreen-prev {
            left: -60px;
          }

          .fullscreen-next {
            right: -60px;
          }

          .fullscreen-nav:hover {
            background: rgba(255, 255, 255, 0.2);
            transform: translateY(-50%) scale(1.1);
          }

          .fullscreen-counter {
            position: absolute;
            bottom: -40px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.6);
            color: ${brandIdentity.colors.white};
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.md};
            border-radius: 20px;
            font-size: ${brandIdentity.typography.sizes.small};
            font-weight: ${brandIdentity.typography.weights.medium};
          }

          .zoom-indicator {
            color: ${brandIdentity.colors.accent};
            font-weight: ${brandIdentity.typography.weights.semibold};
          }

          .fullscreen-instructions {
            position: absolute;
            top: -70px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.6);
            color: ${brandIdentity.colors.white};
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.md};
            border-radius: 12px;
            font-size: ${brandIdentity.typography.sizes.xs};
            text-align: center;
            line-height: 1.4;
            opacity: 0.8;
          }

          /* ===== ESTILOS DEL MODAL DE CANCELACIÓN ===== */
          .cancel-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10001;
            padding: 20px;
            backdrop-filter: blur(4px);
          }

          .cancel-modal-container {
            background: ${brandIdentity.colors.white};
            border-radius: 12px;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
            width: 95%;
            max-width: 500px;
            overflow: hidden;
            animation: modalSlideIn 0.3s ease-out;
          }

          .cancel-modal-header {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: ${brandIdentity.colors.white};
            padding: ${brandIdentity.spacing.lg};
            border-bottom: 1px solid #dc2626;
          }

          .cancel-modal-header h3 {
            margin: 0;
            font-size: ${brandIdentity.typography.sizes.h2};
            font-weight: ${brandIdentity.typography.weights.semibold};
          }

          .cancel-modal-body {
            padding: ${brandIdentity.spacing.lg};
          }

          .cancel-modal-body > p {
            margin: 0 0 ${brandIdentity.spacing.lg} 0;
            color: ${brandIdentity.colors.textDark};
            line-height: 1.6;
          }

          .cancel-reason-input-container {
            margin-bottom: ${brandIdentity.spacing.md};
          }

          .cancel-reason-label {
            display: block;
            margin-bottom: ${brandIdentity.spacing.sm};
            font-weight: ${brandIdentity.typography.weights.semibold};
            color: ${brandIdentity.colors.textDark};
            font-size: ${brandIdentity.typography.sizes.body};
          }

          .required-indicator {
            color: #ef4444;
            font-weight: ${brandIdentity.typography.weights.bold};
          }

          .cancel-reason-textarea {
            width: 100%;
            padding: ${brandIdentity.spacing.sm};
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-family: ${brandIdentity.typography.fontFamily};
            font-size: ${brandIdentity.typography.sizes.body};
            resize: vertical;
            min-height: 100px;
            transition: all 0.2s ease;
          }

          .cancel-reason-textarea:focus {
            border-color: #ef4444;
            outline: none;
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
          }

          .cancel-reason-error {
            color: #ef4444;
            font-size: ${brandIdentity.typography.sizes.small};
            margin-top: ${brandIdentity.spacing.xs};
            margin-bottom: 0;
            font-weight: ${brandIdentity.typography.weights.medium};
          }

          .cancel-modal-actions {
            padding: ${brandIdentity.spacing.md} ${brandIdentity.spacing.lg} ${brandIdentity.spacing.lg};
            display: flex;
            justify-content: flex-end;
            gap: ${brandIdentity.spacing.md};
            border-top: 1px solid #e2e8f0;
          }

          .btn-danger-cancel {
            background-color: #ef4444;
            color: white;
            border: none;
            padding: ${brandIdentity.spacing.sm} ${brandIdentity.spacing.lg};
            border-radius: 8px;
            font-weight: ${brandIdentity.typography.weights.medium};
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.xs};
          }

          .btn-danger-cancel:hover:not(:disabled) {
            background-color: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }

          .btn-danger-cancel:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          /* Notificación de éxito para cancelación */
          .cancel-success-notification {
            position: fixed;
            top: 20px;
            right: 20px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: ${brandIdentity.spacing.md} ${brandIdentity.spacing.lg};
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
            display: flex;
            align-items: center;
            gap: ${brandIdentity.spacing.md};
            z-index: 10002;
            animation: slideInRight 0.3s ease-out;
            min-width: 300px;
          }

          .cancel-success-notification .success-icon {
            background: white;
            color: #10b981;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 1.2rem;
            flex-shrink: 0;
          }

          .cancel-success-notification p {
            margin: 0;
            font-weight: ${brandIdentity.typography.weights.medium};
            font-size: ${brandIdentity.typography.sizes.body};
          }

          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          /* Responsividad del Modal de Cancelación */
          @media (max-width: 768px) {
            .cancel-modal-container {
              max-width: 95vw;
            }

            .cancel-modal-header,
            .cancel-modal-body,
            .cancel-modal-actions {
              padding: ${brandIdentity.spacing.md};
            }

            .cancel-modal-actions {
              flex-direction: column;
            }

            .cancel-modal-actions button {
              width: 100%;
              justify-content: center;
            }

            .cancel-success-notification {
              top: 10px;
              right: 10px;
              left: 10px;
              min-width: auto;
            }
          }


          /* Responsividad del Modal */
          @media (max-width: 1200px) {
            .transaction-modal-container {
              width: 90vw;
              height: 80vh;
            }
            
            .transaction-modal-content {
              grid-template-columns: 400px 1fr;
            }
          }

          @media (max-width: 1024px) {
            .transaction-modal-container {
              width: 95vw;
              height: 85vh;
            }
            
            .transaction-modal-content {
              grid-template-columns: 1fr;
              grid-template-rows: 300px 1fr;
            }
            
            .transaction-images-section {
              border-right: none;
              border-bottom: 1px solid #e2e8f0;
            }
            
            .tabs-navigation {
              flex-wrap: nowrap;
              overflow-x: auto;
              padding: ${brandIdentity.spacing.xs};
            }
            
            .tab-btn {
              padding: ${brandIdentity.spacing.xs} ${brandIdentity.spacing.sm};
              font-size: ${brandIdentity.typography.sizes.xs};
            }
          }

          @media (max-width: 768px) {
            .transaction-modal-container {
              width: 98vw;
              height: 95vh;
              border-radius: 8px;
            }
            
            .transaction-modal-header {
              padding: ${brandIdentity.spacing.md};
              flex-direction: column;
              gap: ${brandIdentity.spacing.sm};
              align-items: flex-start;
            }

            .transaction-modal-actions {
              align-self: flex-end;
              gap: ${brandIdentity.spacing.sm};
            }

            .contract-download-btn span,
            .cancel-transaction-btn span {
              display: none;
            }

            .cancel-transaction-btn {
              padding: ${brandIdentity.spacing.sm};
            }
            
            .transaction-modal-title h2 {
              font-size: ${brandIdentity.typography.sizes.h2};
            }
            
            .transaction-id-badge {
              font-size: ${brandIdentity.typography.sizes.xs};
            }
            
            .transaction-modal-content {
              grid-template-rows: 250px 1fr;
            }
            
            .images-header,
            .images-gallery,
            .tab-content {
              padding: ${brandIdentity.spacing.md};
            }
            
            .tab-btn {
              padding: ${brandIdentity.spacing.xs};
              min-width: 60px;
            }
            
            .tab-label {
              display: none;
            }
            
            .fullscreen-nav {
              width: 40px;
              height: 40px;
              font-size: ${brandIdentity.typography.sizes.body};
            }
            
            .fullscreen-prev {
              left: -50px;
            }
            
            .fullscreen-next {
              right: -50px;
            }
          }

          @media (max-width: 480px) {
            .transaction-modal-overlay {
              padding: 10px;
            }
            
            .transaction-modal-container {
              width: 100vw;
              height: 100vh;
              border-radius: 0;
            }
            
            .fullscreen-nav {
              display: none;
            }
            
            .thumbnails-container {
              display: none;
            }

            .fullscreen-instructions {
              display: none;
            }
          }

          /* Responsividad General */
          @media (max-width: 1024px) {
            .controls-grid {
              grid-template-columns: 1fr 1fr;
              gap: 1rem;
            }
            
            .export-section {
              grid-column: 1 / -1;
            }
            
            .export-controls-vertical {
              flex-direction: row;
              flex-wrap: wrap;
              justify-content: center;
            }
            
            .export-section .btn-full-width {
              flex: 1;
              min-width: 150px;
            }
            
            .search-controls-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
            
            .pagination-controls {
              flex-direction: column;
              gap: 1rem;
            }
            
            .pagination-pages {
              order: 2;
            }
            
            .btn-pagination {
              min-width: 120px;
            }
          }
          
          @media (max-width: 768px) {
            .controls-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
            
            .unified-controls-sticky {
              position: relative;
              top: auto;
            }
            
            .unified-controls-content.visible {
              padding: 1rem;
            }
            
            .export-controls-vertical {
              flex-direction: column;
            }
            
            .export-section .btn-full-width {
              width: 100%;
            }
            
            .search-controls-grid {
              grid-template-columns: 1fr;
            }
            
            .search-input-group {
              flex-direction: column;
            }
            
            .search-input-group input {
              width: 100%;
            }
          }
          
          @media (max-width: 480px) {
            .unified-controls-header {
              padding: 1rem;
            }
            
            .unified-controls-title {
              font-size: 1rem;
            }
            
            .unified-controls-subtitle {
              font-size: 0.8rem;
            }
            
            .section-title {
              font-size: 0.9rem;
            }
            
            .control-section {
              padding: 1rem;
            }
          }

        `}</style>
    </div>
  );
}

export default Transacciones;